import { useUpdatePetitionMutation } from "@codegen/index";
import { UpdatePetitionByIdInput } from "@codegen/schema";
import {
  FormContainer,
  OnboardingContainer,
  OnboardingFormField,
} from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { OnboardingPageType } from "@pages/onboarding/types";
import { useBeneficiaryWorkflowContext } from "@pages/onboarding/workflows/provider";
import { useDebounceCallback } from "@react-hook/debounce";
import { useLogError } from "@utils/error";
import { formatVisaClass } from "@utils/parsers";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
// Schema definition
const formSchema = z.object({
  hasVisaDenial: z.enum(["Yes", "No"], {
    required_error: "Please select Yes or No",
  }),
  hasJ1J2History: z.enum(["Yes", "No"], {
    required_error: "Please select Yes or No",
  }),
});

export type ImmigrationQuestionsFormType = z.infer<typeof formSchema>;

export const ImmigrationQuestionsForm: React.FC<
  OnboardingPageType & {
    immigrationData?: ImmigrationQuestionsFormType;
    refetch: () => void;
  }
> = ({
  petition,
  onSubmit,
  onBack,
  immigrationData,
  refetch,
  onSkipForNow,
  exhibit,
  moduleProgress,
  currentStep,
  showSkipForNow,
  hideExitButton,
}) => {
  const { isReadOnly } = useReadOnlyMode();
  const { refetchPetitions } = useBeneficiaryWorkflowContext();
  const [status, setStatus] = useState<
    "idle" | "loading" | "error" | "success"
  >("idle");

  // Track pending save operations to avoid race conditions
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  const methods = useForm<ImmigrationQuestionsFormType>({
    defaultValues: immigrationData || {
      hasVisaDenial: undefined,
      hasJ1J2History: undefined,
    },
    resolver: zodResolver(formSchema),
  });

  const { control, setError } = methods;
  const logError = useLogError();
  const [updatePetitionMutation, { loading: saving }] =
    useUpdatePetitionMutation();

  const watchedValues = useWatch({
    control,
  });

  const performMutation = useCallback(
    async (data: ImmigrationQuestionsFormType) => {
      // Get existing metadata and merge with new data
      const existingMetadata = petition.metadata
        ? JSON.parse(petition.metadata)
        : {};
      const newMetadata = {
        ...existingMetadata,
        hasVisaDenial: data.hasVisaDenial,
        hasJ1J2History: data.hasJ1J2History,
      };

      const { errors: updatePetitionError } = await updatePetitionMutation({
        variables: {
          input: {
            id: petition.id,
            petitionPatch: {
              metadata: JSON.stringify(newMetadata),
            },
          } as UpdatePetitionByIdInput,
        },
      });

      if (updatePetitionError != null) {
        throw updatePetitionError;
      }

      await refetch();
      await refetchPetitions();
    },
    [
      petition.id,
      petition.metadata,
      refetch,
      refetchPetitions,
      updatePetitionMutation,
    ]
  );

  const handleSave = useCallback(
    async (data: ImmigrationQuestionsFormType) => {
      const savePromise = (async () => {
        try {
          setStatus("loading");
          await performMutation(data);

          setStatus("success");
        } catch (exception) {
          setStatus("error"); // Show error for auto-save failure
          if (exception instanceof Error) {
            setError("root.serverError", {
              type: exception.name,
              message: exception.message,
            });
          }
          logError(exception, {
            message: "Error auto-saving immigration data",
          });
        } finally {
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      await savePromise;
    },
    [performMutation, logError, setError]
  );

  const debouncedSave = useDebounceCallback(handleSave, 300);

  useEffect(() => {
    // Only call debouncedSave if the form data is valid
    const parseResult = formSchema.safeParse(watchedValues);
    if (parseResult.success) {
      debouncedSave(parseResult.data);
      console.log("debouncedSave", parseResult.data);
    } else {
      console.log("debouncedSave", parseResult.error);
    }
  }, [watchedValues, debouncedSave]);

  const handleSubmit = useCallback(
    async (data: ImmigrationQuestionsFormType) => {
      try {
        // Wait for any pending auto-save to complete
        if (pendingSaveRef.current) {
          await pendingSaveRef.current;
        }

        setStatus("loading");
        await performMutation(data);
        setStatus("success");
        onSubmit(data);
      } catch (exception) {
        setStatus("error");
        if (exception instanceof Error) {
          setError("root.serverError", {
            type: exception.name,
            message: exception.message,
          });
        }
        logError(exception, {
          message: "Error submitting immigration data",
        });
      }
    },
    [performMutation, onSubmit, logError, setError]
  );

  return (
    <FormProvider {...methods}>
      <OnboardingContainer
        title={exhibit.name}
        subtitle="Immigration Questions"
        description="Please provide information about your immigration history"
        progress={moduleProgress}
        onSubmit={methods.handleSubmit(handleSubmit) as any}
        onBack={onBack}
        onSkipForNow={onSkipForNow}
        exhibit={exhibit}
        name="immigration-questions-form"
        isLoading={saving}
        currentStep={currentStep}
        showSkipForNow={showSkipForNow}
        hideExitButton={hideExitButton}
      >
        <FormContainer
          status={status}
          errorMessage="There was a problem saving your immigration information. Please try again."
        >
          <OnboardingFormField
            name="hasVisaDenial"
            label={`Have you ever been denied for a ${formatVisaClass(
              petition.visaClass
            )} in the last 7 years?`}
            type="radio"
            required={true}
            radioOptions={[
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" },
            ]}
            disabled={isReadOnly}
          />

          <OnboardingFormField
            name="hasJ1J2History"
            label="Have you ever had a J-1 exchange visitor or J-2 dependent before?"
            type="radio"
            required={true}
            radioOptions={[
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" },
            ]}
            disabled={isReadOnly}
          />
        </FormContainer>
      </OnboardingContainer>
    </FormProvider>
  );
};

export const ImmigrationQuestionsFormController: React.FC<
  OnboardingPageType
> = (props) => {
  // Get existing data from petition metadata
  const immigrationData = React.useMemo(() => {
    if (props.petition?.metadata) {
      const metadata = JSON.parse(props.petition.metadata);
      return {
        hasVisaDenial: metadata.hasVisaDenial,
        hasJ1J2History: metadata.hasJ1J2History,
      };
    }
    return undefined;
  }, [props.petition?.metadata]);

  // No need for loading state since data comes from petition prop
  return (
    <ImmigrationQuestionsForm
      {...props}
      immigrationData={immigrationData}
      refetch={() => {}} // No need to refetch since we're using petition metadata
    />
  );
};
